import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import {
  Settings,
  Save,
  X
} from 'lucide-react';

// Composant pour un élément d'étape (read-only avec toggle actif/inactif uniquement)
function StepItem({ step, onUpdateStep }) {
  const canBeToggled = step.canBeDisabled !== false;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center space-x-4 flex-1">
        {/* Numéro d'ordre */}
        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-blue-600">{step.order}</span>
        </div>

        {/* Nom de l'étape (read-only) */}
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-900">{step.name}</span>
          {!canBeToggled && (
            <span className="ml-2 text-xs text-gray-500 italic">(obligatoire)</span>
          )}
        </div>

        {/* Toggle actif/inactif */}
        <div className="flex items-center">
          {canBeToggled ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={step.active}
                onChange={(e) => onUpdateStep(step._id, { active: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">
                {step.active ? 'Actif' : 'Inactif'}
              </span>
            </label>
          ) : (
            <span className="text-sm font-medium text-green-600">Toujours actif</span>
          )}
        </div>
      </div>
    </div>
  );
}

const EditProcessBizDev = ({ process, isOpen, onClose, onProcessUpdated }) => {
  const API = useContext(ApiContext);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true
  });
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  // Bloquer le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Initialiser les données du formulaire
  useEffect(() => {
    if (isOpen && process) {
      setFormData({
        name: process.name || '',
        description: process.description || '',
        active: process.active !== undefined ? process.active : true
      });
      setSteps(process.steps ? [...process.steps].sort((a, b) => a.order - b.order) : []);
    }
  }, [isOpen, process]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateStep = (stepId, updates) => {
    setSteps(prev => prev.map(step =>
      step._id === stepId ? { ...step, ...updates } : step
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Le nom du processus est obligatoire');
      return;
    }

    setLoading(true);
    try {
      // Mettre à jour les informations de base du processus
      await API.put(`/api/process-bizdev/${process._id}`, formData);

      // Mettre à jour uniquement le statut actif/inactif des étapes
      for (const step of steps) {
        await API.put(`/api/process-bizdev/${process._id}/steps/${step._id}`, {
          active: step.active
        });
      }

      toast.success('Processus mis à jour avec succès');

      if (onProcessUpdated) {
        onProcessUpdated();
      }

      onClose();
    } catch (error) {
      console.error('Error updating process:', error);
      toast.error('Erreur lors de la mise à jour du processus');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {/* Overlay sombre avec effet de flou */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal avec contrainte de hauteur */}
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 relative z-10 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Settings className="text-white" size={18} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white m-0">Modifier le processus</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-6" id="edit-process-form">
            {/* Informations de base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du projet *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Prospection Q1 2024"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">Projet actif</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Description du projet..."
              />
            </div>

            {/* Gestion des étapes */}
            <div>
              <div className="mb-4">
                <h4 className="text-lg font-medium text-gray-900">Étapes du processus</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Les étapes sont fixes. Vous pouvez activer ou désactiver les étapes optionnelles.
                </p>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {steps.map((step) => (
                  <StepItem
                    key={step._id}
                    step={step}
                    onUpdateStep={handleUpdateStep}
                  />
                ))}
              </div>

              {steps.length === 0 && (
                <p className="text-gray-500 text-center py-8">
                  Aucune étape définie.
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Buttons - fixés en bas */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="edit-process-form"
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            <span>{loading ? 'Mise à jour...' : 'Mettre à jour'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProcessBizDev;
